import { useEffect , useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { Outlet } from 'react-router-dom';
import {
  useBranding,
  useSaveUiSettings,
  useUiSettings,
  useUserProfile,
} from '@/features/auth/api';
import { useAuthStore } from '@/lib/auth/store';

import { useAclSettingDetail, useEffectiveAclId } from '@/features/acl/api';
import { usePermissionStore } from '@/lib/permissions/store';
import { USER_ROLES } from '@/lib/constants/roles';
import { serializeTheme, useThemeStore } from '@/lib/theme/store';
import type { SerializedTheme } from '@/lib/theme/store';
import { AclRouteGuard } from './AclRouteGuard';
import { Header } from './Header';
import { Sidebar, TopBar } from './Sidebar';
import { SidebarDocker, SidebarDragHandle } from './SidebarDocker';
import { hexToHsl, hslToString } from '@/lib/theme/hsl-utils';


/** Debounce window between a theme-store change and the server save. */
const SAVE_DEBOUNCE_MS = 1500;

/**
 * Parse a JSON blob safely. Returns `null` on any error so callers can keep
 * existing state rather than blowing up on corrupted persisted prefs.
 */
function safeParseTheme(json: string | null | undefined): Partial<SerializedTheme> | null {
  if (!json) return null;
  try {
    const parsed = JSON.parse(json);
    if (!parsed || typeof parsed !== 'object') return null;

    // Map legacy field names (old Java format) → new theme store keys
    const mapped: Record<string, unknown> = { ...parsed };
    if (parsed.themeColor && !parsed.custom) {
      const hsl = hexToHsl(parsed.themeColor as string);
      mapped.custom = { primary: hsl ? hslToString(hsl) : null };
      delete mapped.themeColor;
    }
    if (parsed.sidebarPosition && !parsed.sidebarPlacement) {
      mapped.sidebarPlacement = parsed.sidebarPosition;
      delete mapped.sidebarPosition;
    }

    return mapped as Partial<SerializedTheme>;
  } catch {
    return null;
  }
}

/**
 * Authenticated shell. The sidebar placement (`left` / `right` / `top`) is
 * read from the theme store; the layout reorders accordingly. The whole
 * shell is wrapped in <SidebarDocker> so the drag handle in the sidebar
 * can dock to any edge.
 */
export function AppShell() {
  const placement = useThemeStore((s) => s.sidebarPlacement);
  const hydrateFromServer = useThemeStore((s) => s.hydrateFromServer);
  const setHydrating = useThemeStore((s) => s.setHydrating);
  const branding = useBranding();
  const uiSettings = useUiSettings();
  const saveUiSettings = useSaveUiSettings();
  const profile = useUserProfile();


  // --- Sub-super-admin: fetch ACL detail and populate permission + moduleListing stores ---
  const isSubSuperAdmin = useAuthStore((s) => s.isSubSuperAdmin());
  const aclId = profile.data?.aclId ?? null;
  const aclDetail = useAclSettingDetail(isSubSuperAdmin ? aclId : null);

  // Block rendering child routes for a sub-superadmin until BOTH the
  // profile (which carries aclId) and the ACL detail itself have
  // settled. Without this, usePermission() sees an empty perms tree
  // during the load window and every ACL-gated button/page briefly
  // looks denied — or worse, a permission-gated page that redirects on
  // denial (<Navigate>) kicks the user out before real permissions load.
  const aclStillLoading =
    isSubSuperAdmin && (profile.isLoading || (aclId != null && aclDetail.isLoading));

  const setPerms = usePermissionStore((s) => s.setPerms);
  const setModuleListing = usePermissionStore((s) => s.setModuleListing);

  // --- Reseller / end user: inherit the nearest sub-superadmin's ACL for sidebar gating ---
  const authRole = useAuthStore((s) => s.role);
  const authUserId = useAuthStore((s) => s.userId);
  const isReseller = authRole === USER_ROLES.ADMIN;
  const isResellerOrUser = isReseller || authRole === USER_ROLES.USER;
  const effectiveAcl = useEffectiveAclId(isResellerOrUser ? authUserId : null);
  const inheritedAclId = effectiveAcl.data?.aclId ?? null;
  // /get/acl/setting/:id is allowed for ADMIN (reseller) only, not USER.
  const inheritedAclDetail = useAclSettingDetail(isResellerOrUser ? inheritedAclId : null);
  const setInheritedPerms = usePermissionStore((s) => s.setInheritedPerms);
  const inheritedPerms = usePermissionStore((s) => s.inheritedPerms);

  useEffect(() => {
    if (!isSubSuperAdmin || !aclDetail.data) return;

    // Parse moduleOperation → permission tree
    const rawOp = aclDetail.data.moduleOperation;
    if (rawOp) {
      try {
        const parsed = typeof rawOp === 'string' ? JSON.parse(rawOp) : rawOp;
        // Normalize children arrays into objects (same as legacy normalizePermissions)
        const normalize = (node: Record<string, unknown>): Record<string, unknown> => {
          if (Array.isArray(node.children)) {
            const obj: Record<string, unknown> = {};
            for (const child of node.children as Record<string, unknown>[]) {
              const [key, val] = Object.entries(child)[0] ?? [];
              if (key) obj[key] = normalize((val ?? {}) as Record<string, unknown>);
            }
            node.children = obj;
          }
          return node;
        };
        const normalized: Record<string, unknown> = {};
        for (const [key, val] of Object.entries(parsed)) {
          normalized[key] = normalize((val ?? {}) as Record<string, unknown>);
        }
        setPerms(normalized as Parameters<typeof setPerms>[0]);
      } catch { /* ignore parse errors */ }
    }

    // Parse moduleListingData → moduleListing store
    const rawListing = aclDetail.data.moduleListingData;
    if (rawListing) {
      try {
        const parsed = typeof rawListing === 'string' ? JSON.parse(rawListing) : rawListing;
        setModuleListing(parsed as Record<string, unknown>);
      } catch { /* ignore */ }
    }
  }, [isSubSuperAdmin, aclDetail.data, setPerms, setModuleListing]);

  useEffect(() => {
    if (!isResellerOrUser) {
      setInheritedPerms(null);
      return;
    }
    // Loaded fine, but no sub-superadmin above this user → no restriction.
    if (effectiveAcl.isSuccess && inheritedAclId == null) {
      setInheritedPerms(null);
      return;
    }
    const rawOp = inheritedAclDetail.data?.moduleOperation;
    if (!rawOp) return;
    try {
      const parsed = typeof rawOp === 'string' ? JSON.parse(rawOp) : rawOp;
      setInheritedPerms(parsed as Parameters<typeof setInheritedPerms>[0]);
    } catch {
      /* ignore parse errors → stay unrestricted */
    }
  }, [
    isResellerOrUser,
    effectiveAcl.isSuccess,
    inheritedAclId,
    inheritedAclDetail.data,
    setInheritedPerms,
  ]);

  // Reflect the branded company name in the document title.
  useEffect(() => {
    if (branding.data?.company) {
      document.title = branding.data.company;
    }
  }, [branding.data?.company]);

  /* ---- Hydrate the theme store on login ---------------------------------
   *
   * Two layers, applied in order so later overlays win:
   *   1. Per-domain `uiDefaults` from the branding endpoint.
   *   2. Per-user `uiSettings` from /api/obd/user/{id}/ui/settings.
   *
   * We toggle `isHydrating` while either query is loading so the debounced
   * save subscriber below doesn't echo the hydration straight back to the
   * server. */
  useEffect(() => {
    setHydrating(uiSettings.isLoading || branding.isLoading);
  }, [uiSettings.isLoading, branding.isLoading, setHydrating]);

  useEffect(() => {
    const defaults = safeParseTheme(branding.data?.uiDefaults);
    if (defaults) hydrateFromServer(defaults);
  }, [branding.data?.uiDefaults, hydrateFromServer]);

  useEffect(() => {
    const overlay = safeParseTheme(uiSettings.data?.json);
    if (overlay) hydrateFromServer(overlay);
  }, [uiSettings.data?.json, hydrateFromServer]);

  /* ---- Debounced save on every theme-store change ---------------------- */
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSentRef = useRef<string | null>(null);

  useEffect(() => {
    // Subscribe outside React's render cycle so we don't trigger a re-render
    // on every keystroke-equivalent setter. The closure captures the latest
    // mutation handle via the ref pattern.
    const unsub = useThemeStore.subscribe((state, prev) => {
      if (state.isHydrating) return;
      // Skip if the persisted slice didn't actually change (e.g. only
      // isHydrating flipped).
      const next = JSON.stringify(serializeTheme(state));
      const prevSerialized = JSON.stringify(serializeTheme(prev));
      if (next === prevSerialized) return;
      if (next === lastSentRef.current) return;

      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        lastSentRef.current = next;
        saveUiSettings.mutate(next);
      }, SAVE_DEBOUNCE_MS);
    });
    return () => {
      unsub();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [saveUiSettings]);

  const brand = {
    logoUrl: branding.data?.logoUrl ?? null,
    company: branding.data?.company ?? null,
  };

  const isHorizontal = placement === 'top';
  const isRight = placement === 'right';

  /*
   * Layout strategy — fixed viewport height with no page-level scrolling.
   * The root container is h-screen + overflow-hidden so children (mainly
   * the DataTable) can host their own internal scroll regions and pin
   * sticky headers reliably. Pages that need scrollable content (e.g.
   * UserProfilePage) wrap themselves in an overflow-y-auto container.
   *
   * The previous min-h-screen layout let the page itself scroll, which
   * meant sticky table headers (positioned relative to the inner
   * overflow-x container) could not pin to the viewport during page
   * scroll. Switching to fixed-viewport + internal scroll fixes that
   * once and for all.
   */
  if (aclStillLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <SidebarDocker>
      <div className="flex h-screen overflow-hidden bg-background">
        {!isHorizontal && !isRight && (
          <Sidebar brand={brand} showDragHandle dragHandle={<SidebarDragHandle />} />
        )}
        <div className="flex min-w-0 flex-1 flex-col">
          <Header
            username={profile.data?.username ?? profile.data?.name ?? ''}
            voiceCredits={profile.data?.credits}
            ttsCredits={profile.data?.ttsCredits}
            showTtsBalance={
              !isReseller ||
              !inheritedPerms ||
              Boolean(inheritedPerms['TTS Plans']?.view_permission)
            }

          />
          {isHorizontal && <TopBar brand={brand} dragHandle={<SidebarDragHandle />} />}
          <main className="flex min-h-0 flex-1 flex-col">
            <AclRouteGuard>
              <Outlet />
            </AclRouteGuard>
          </main>
        </div>
        {!isHorizontal && isRight && (
          <Sidebar brand={brand} showDragHandle dragHandle={<SidebarDragHandle />} />
        )}
      </div>
    </SidebarDocker>
  );
}
